<template>
  <v-container fluid class="ncrm-leads-home">
    <NcrmHomePageHeader
      icon="mdi-filter-multiple-outline"
      :title="$t('ncrm.leads.home.title')"
      :subtitle="$t('ncrm.leads.home.subtitle')"
      data-tour="leads-header"
    >
      <v-btn variant="text" color="primary" :loading="isLoading" @click="loadHome">
        <v-icon start>mdi-refresh</v-icon>
        {{ $t("ncrm.leads.actions.refresh") }}
      </v-btn>
      <v-btn variant="text" color="primary" @click="widgetManagerDialog = true">
        <v-icon start>mdi-view-dashboard-edit-outline</v-icon>
        {{ $t("ncrm.leads.home.widgets.configure") }}
      </v-btn>
      <v-btn color="primary" data-tour="leads-create-board" @click="openCreateBoard">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.leads.actions.createBoard") }}
      </v-btn>
    </NcrmHomePageHeader>
    <NcrmHomeKpiCards class="mt-3" :items="globalKpis" data-tour="leads-kpi" />

    <v-row class="mt-2" dense data-tour="leads-widgets">
      <v-col cols="12" v-if="!activeWidgetItems.length">
        <v-alert type="info">
          {{ $t("ncrm.leads.home.widgets.noneSelected") }}
        </v-alert>
      </v-col>

      <v-col
        v-for="widget in activeWidgetItems"
        :key="widget.key"
        :cols="widget.layout.cols"
        :md="widget.layout.md"
        :lg="widget.layout.lg"
      >
        <v-card
          v-if="widget.key === 'trend'"
          class="ncrm-leads-home__widget-card h-100"
        >
          <div class="ncrm-leads-home__widget-header">
            <div class="d-flex align-center ga-2">
              <v-icon size="15" class="ncrm-leads-home__widget-header-icon">mdi-chart-bar</v-icon>
              <span class="ncrm-leads-home__widget-title">{{ $t("ncrm.leads.home.trend.title") }}</span>
            </div>
            <v-chip size="x-small" color="primary">
              {{ $t("ncrm.leads.home.trend.subtitle") }}
            </v-chip>
          </div>
          <v-card-text>
            <v-skeleton-loader v-if="widgetsLoading" type="article" />
            <div v-else-if="!trendSeries.length" class="text-body-2 text-medium-emphasis">
              {{ $t("ncrm.leads.home.trend.empty") }}
            </div>
            <div v-else>
              <div class="text-caption text-medium-emphasis mb-3">
                {{
                  $t("ncrm.leads.home.trend.delta", {
                    delta: formatSignedNumber(trendDelta.delta_total),
                    percent: trendDelta.delta_percent_label,
                  })
                }}
              </div>
              <div
                v-for="item in trendSeries"
                :key="item.date"
                class="d-flex align-center ga-3 mb-2 ncrm-leads-home__trend-row"
              >
                <div class="text-caption text-medium-emphasis ncrm-leads-home__trend-date">
                  {{ item.label }}
                </div>
                <div class="flex-grow-1">
                  <v-progress-linear
                    :model-value="item.total_percent"
                    color="primary"
                    height="8"
                    rounded
                  />
                </div>
                <div class="text-caption ncrm-text-strong ncrm-leads-home__trend-total">
                  {{ formatNumber(item.total) }}
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-else-if="widget.key === 'statusDistribution'"
          class="ncrm-leads-home__widget-card h-100"
        >
          <div class="ncrm-leads-home__widget-header">
            <div class="d-flex align-center ga-2">
              <v-icon size="15" class="ncrm-leads-home__widget-header-icon">mdi-chart-donut</v-icon>
              <span class="ncrm-leads-home__widget-title">{{ $t("ncrm.leads.home.statusDistribution.title") }}</span>
            </div>
          </div>
          <v-card-text>
            <v-skeleton-loader v-if="widgetsLoading" type="chip@6" />
            <div v-else-if="!statusDistribution.length" class="text-body-2 text-medium-emphasis">
              {{ $t("ncrm.leads.home.statusDistribution.empty") }}
            </div>
            <div v-else class="d-flex flex-wrap ga-2">
              <v-chip
                v-for="item in statusDistribution"
                :key="item.status"
                color="info"
              >
                {{ statusLabel(item.status) }} · {{ formatNumber(item.count) }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-else-if="widget.key === 'boardMetrics'"
          class="ncrm-leads-home__widget-card h-100"
        >
          <div class="ncrm-leads-home__widget-header">
            <div class="d-flex align-center ga-2">
              <v-icon size="15" class="ncrm-leads-home__widget-header-icon">mdi-view-dashboard-outline</v-icon>
              <span class="ncrm-leads-home__widget-title">{{ $t("ncrm.leads.home.boardWidget.title") }}</span>
            </div>
          </div>
          <v-card-text>
            <NcrmHomeBoardMetrics
              :loading="isLoading"
              :items="boardWidgetItems"
              :empty-message="$t('ncrm.leads.home.boardWidget.empty')"
              :eyebrow-label="$t('ncrm.leads.home.headers.name')"
              :open-label="$t('ncrm.leads.actions.openBoard')"
              @open="openBoard"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="mt-4" data-tour="leads-boards">
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="boards"
          :loading="boardsLoading"
          item-key="id"
          class="elevation-0 ncrm-leads-home__table"
          density="comfortable"
          hover
          :row-props="tableRowProps"
          @click:row="handleBoardRowClick"
        >
          <template #item.name="{ item }">
            <div class="d-flex align-center ga-2">
              <v-icon size="15" class="ncrm-leads-home__table-cell-icon">mdi-view-column-outline</v-icon>
              <span class="text-body-2 ncrm-text-strong">{{ item.name }}</span>
            </div>
          </template>

          <template #item.entity_scope="{ item }">
            <v-chip color="primary">
              {{ entityScopeLabel(item.entity_scope) }}
            </v-chip>
          </template>

          <template #item.open_entry_count="{ item }">
            {{ formatNumber(boardMetricValue(item.id, "open_entries", item.open_entry_count)) }}
          </template>

          <template #item.overdue_followups="{ item }">
            <span :class="boardMetricValue(item.id, 'overdue_followups') > 0 ? 'ncrm-leads-home__table-overdue' : ''">
              {{ formatNumber(boardMetricValue(item.id, "overdue_followups")) }}
            </span>
          </template>

          <template #item.touched_today="{ item }">
            {{ formatNumber(boardMetricValue(item.id, "touched_today")) }}
          </template>

          <template #item.qualified_week="{ item }">
            {{ formatNumber(boardMetricValue(item.id, "qualified_week")) }}
          </template>

          <template #item.updated_at="{ item }">
            <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
              <v-icon size="12" style="opacity: 0.55">mdi-clock-outline</v-icon>
              {{ formatDateTime(item.updated_at) }}
            </div>
          </template>

          <template #item.row_nav>
            <v-icon size="16" class="ncrm-leads-home__table-chevron">mdi-chevron-right</v-icon>
          </template>

          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.leads.home.empty") }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <NcrmLeadBoardWizard
      v-model="createBoardDialog"
      :title="$t('ncrm.leads.wizard.createTitle')"
      :subtitle="$t('ncrm.leads.wizard.createSubtitle')"
      :loading="createBoardLoading"
      @submit="createBoard"
    />

    <v-dialog v-model="widgetManagerDialog" max-width="760">
      <v-card class="ncrm-leads-home__widget-manager">
        <v-card-title>{{ $t("ncrm.leads.home.widgets.manageTitle") }}</v-card-title>
        <v-divider />
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2 ncrm-text-strong mb-2">
                {{ $t("ncrm.leads.home.widgets.activeTitle") }}
              </div>
              <v-alert v-if="!activeWidgetItems.length" type="info" density="compact">
                {{ $t("ncrm.leads.home.widgets.emptyActive") }}
              </v-alert>
              <v-list v-else density="compact" class="ncrm-leads-home__widget-list">
                <v-list-item
                  v-for="(item, index) in activeWidgetItems"
                  :key="`active-${item.key}`"
                  :title="item.label"
                >
                  <template #append>
                    <div class="d-flex align-center ga-1">
                      <v-btn
                        icon="mdi-arrow-up"
                        size="x-small"
                        variant="text"
                        :disabled="index === 0"
                        :aria-label="$t('ncrm.leads.home.widgets.moveUp')"
                        @click="moveWidget(item.key, -1)"
                      />
                      <v-btn
                        icon="mdi-arrow-down"
                        size="x-small"
                        variant="text"
                        :disabled="index === activeWidgetItems.length - 1"
                        :aria-label="$t('ncrm.leads.home.widgets.moveDown')"
                        @click="moveWidget(item.key, 1)"
                      />
                      <v-btn
                        icon="mdi-close"
                        size="x-small"
                        variant="text"
                        color="error"
                        :aria-label="$t('ncrm.leads.home.widgets.remove')"
                        @click="removeWidget(item.key)"
                      />
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-subtitle-2 ncrm-text-strong mb-2">
                {{ $t("ncrm.leads.home.widgets.availableTitle") }}
              </div>
              <v-alert v-if="!availableWidgetItems.length" type="success" density="compact">
                {{ $t("ncrm.leads.home.widgets.emptyAvailable") }}
              </v-alert>
              <v-list v-else density="compact" class="ncrm-leads-home__widget-list">
                <v-list-item
                  v-for="item in availableWidgetItems"
                  :key="`available-${item.key}`"
                  :title="item.label"
                >
                  <template #append>
                    <v-btn
                      icon="mdi-plus"
                      size="x-small"
                      variant="text"
                      color="primary"
                      :aria-label="$t('ncrm.leads.home.widgets.add')"
                      @click="addWidget(item.key)"
                    />
                  </template>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="widgetManagerDialog = false">
            {{ $t("app.actions.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import NcrmLeadBoardWizard from "@/components/ncrm/leads/NcrmLeadBoardWizard.vue";
import NcrmHomeBoardMetrics from "@/components/ncrm/shared/NcrmHomeBoardMetrics.vue";
import NcrmHomeKpiCards from "@/components/ncrm/shared/NcrmHomeKpiCards.vue";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import { normalizeNcrmLeadEntityScope } from "@/Utile/ncrmLeads.mjs";
import { formatDisplayNumber, formatDisplayDayMonth } from "@/Utile/displayFormat.mjs";

const LEADS_HOME_WIDGET_STORAGE_KEY = "ncrm.leads.home.widgets.v1";
const LEADS_HOME_WIDGETS_CATALOG = Object.freeze([
  Object.freeze({
    key: "trend",
    labelKey: "ncrm.leads.home.widgets.trend",
    layout: Object.freeze({ cols: 12, md: 12, lg: 8 }),
  }),
  Object.freeze({
    key: "statusDistribution",
    labelKey: "ncrm.leads.home.widgets.statusDistribution",
    layout: Object.freeze({ cols: 12, md: 12, lg: 4 }),
  }),
  Object.freeze({
    key: "boardMetrics",
    labelKey: "ncrm.leads.home.widgets.boardMetrics",
    layout: Object.freeze({ cols: 12, md: 12, lg: 12 }),
  }),
]);
const LEADS_HOME_WIDGET_KEYS = Object.freeze(LEADS_HOME_WIDGETS_CATALOG.map((item) => item.key));
const LEADS_HOME_DEFAULT_ACTIVE_WIDGET_KEYS = Object.freeze([...LEADS_HOME_WIDGET_KEYS]);

const cloneDefaultActiveWidgetKeys = () => [...LEADS_HOME_DEFAULT_ACTIVE_WIDGET_KEYS];
const parseNumeric = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export default {
  name: "NcrmLeadsHome",
  components: {
    NcrmLeadBoardWizard,
    NcrmHomeBoardMetrics,
    NcrmHomeKpiCards,
    NcrmHomePageHeader,
  },
  data() {
    return {
      boardsLoading: false,
      widgetsLoading: false,
      boards: [],
      homeSummary: {
        totals: {
          boards: 0,
          total_entries: 0,
          open_entries: 0,
          overdue_followups: 0,
          qualified_today: 0,
          qualified_week: 0,
        },
        status_distribution: [],
        board_metrics: [],
      },
      homeTrend: {
        totals: {
          delta_total: 0,
          delta_percent: null,
        },
        series: [],
      },
      activeWidgetKeys: cloneDefaultActiveWidgetKeys(),
      widgetManagerDialog: false,
      createBoardDialog: false,
      createBoardLoading: false,
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
    };
  },
  computed: {
    isLoading() {
      return this.boardsLoading || this.widgetsLoading;
    },
    widgetCatalog() {
      return LEADS_HOME_WIDGETS_CATALOG.map((item) => ({
        key: item.key,
        label: this.$t(item.labelKey),
        layout: item.layout,
      }));
    },
    activeWidgetItems() {
      const activeKeys = this.activeWidgetKeys.filter((key) => LEADS_HOME_WIDGET_KEYS.includes(key));
      return activeKeys
        .map((key) => this.widgetCatalog.find((item) => item.key === key))
        .filter(Boolean);
    },
    availableWidgetItems() {
      const activeSet = new Set(this.activeWidgetItems.map((item) => item.key));
      return this.widgetCatalog.filter((item) => !activeSet.has(item.key));
    },
    headers() {
      return [
        { title: this.$t("ncrm.leads.home.headers.name"), key: "name" },
        { title: this.$t("ncrm.leads.home.headers.entityScope"), key: "entity_scope", sortable: false },
        { title: this.$t("ncrm.leads.home.headers.stages"), key: "stage_count", align: "center" },
        { title: this.$t("ncrm.leads.home.headers.openEntries"), key: "open_entry_count", align: "end" },
        { title: this.$t("ncrm.leads.home.headers.overdue"), key: "overdue_followups", align: "center", sortable: false },
        { title: this.$t("ncrm.leads.home.headers.touchedToday"), key: "touched_today", align: "center", sortable: false },
        { title: this.$t("ncrm.leads.home.headers.qualifiedWeek"), key: "qualified_week", align: "center", sortable: false },
        { title: this.$t("ncrm.leads.home.headers.totalEntries"), key: "entry_count", align: "end" },
        { title: this.$t("ncrm.leads.home.headers.updatedAt"), key: "updated_at" },
        { title: "", key: "row_nav", sortable: false, width: "48px" },
      ];
    },
    globalKpis() {
      const totals = this.homeSummary?.totals || {};
      return [
        {
          key: "open_entries",
          label: this.$t("ncrm.leads.home.kpi.openEntries"),
          value: parseNumeric(totals.open_entries, 0),
          icon: "mdi-filter-outline",
          color: "primary",
        },
        {
          key: "overdue_followups",
          label: this.$t("ncrm.leads.home.kpi.overdueFollowups"),
          value: parseNumeric(totals.overdue_followups, 0),
          icon: "mdi-alert-circle-outline",
          color: "error",
        },
        {
          key: "qualified_today",
          label: this.$t("ncrm.leads.home.kpi.qualifiedToday"),
          value: parseNumeric(totals.qualified_today, 0),
          icon: "mdi-calendar-today",
          color: "info",
        },
        {
          key: "qualified_week",
          label: this.$t("ncrm.leads.home.kpi.qualifiedWeek"),
          value: parseNumeric(totals.qualified_week, 0),
          icon: "mdi-chart-line",
          color: "success",
        },
      ];
    },
    statusDistribution() {
      const rows = Array.isArray(this.homeSummary?.status_distribution) ? this.homeSummary.status_distribution : [];
      return rows
        .map((row) => ({
          status: String(row?.status || "").trim().toLowerCase(),
          count: parseNumeric(row?.count, 0),
        }))
        .filter((row) => row.status)
        .sort((left, right) => right.count - left.count);
    },
    boardMetricMap() {
      const map = new Map();
      const items = Array.isArray(this.homeSummary?.board_metrics) ? this.homeSummary.board_metrics : [];
      for (const item of items) {
        const boardId = Number(item?.board_id || 0);
        if (!boardId) continue;
        map.set(boardId, {
          entry_count: parseNumeric(item?.entry_count, 0),
          open_entries: parseNumeric(item?.open_entries, 0),
          overdue_followups: parseNumeric(item?.overdue_followups, 0),
          touched_today: parseNumeric(item?.touched_today, 0),
          qualified_week: parseNumeric(item?.qualified_week, 0),
          board_name: String(item?.board_name || "").trim(),
          entity_scope: String(item?.entity_scope || "").trim(),
        });
      }
      return map;
    },
    boardWidgetItems() {
      const items = [];
      const seen = new Set();

      for (const board of this.boards) {
        const boardId = Number(board?.id || 0);
        if (!boardId) continue;
        const metrics = this.boardMetricMap.get(boardId) || {};
        items.push({
          id: boardId,
          name: String(board?.name || "").trim() || metrics.board_name || `#${boardId}`,
          badgeText: this.entityScopeLabel(String(board?.entity_scope || "").trim() || metrics.entity_scope || "both"),
          primaryLabel: this.$t("ncrm.leads.home.boardWidget.metrics.openEntries"),
          primaryValue: parseNumeric(metrics.open_entries, parseNumeric(board?.open_entry_count, 0)),
          primaryIcon: "mdi-filter-outline",
          primaryColor: "primary",
          metrics: [
            {
              key: "entry_count",
              label: this.$t("ncrm.leads.home.headers.totalEntries"),
              value: parseNumeric(metrics.entry_count, board?.entry_count),
              icon: "mdi-database-outline",
              color: "quaternary",
            },
            {
              key: "overdue_followups",
              label: this.$t("ncrm.leads.home.boardWidget.metrics.overdueFollowups"),
              value: parseNumeric(metrics.overdue_followups, 0),
              icon: "mdi-alert-circle-outline",
              color: "error",
            },
            {
              key: "touched_today",
              label: this.$t("ncrm.leads.home.boardWidget.metrics.touchedToday"),
              value: parseNumeric(metrics.touched_today, 0),
              icon: "mdi-clock-outline",
              color: "info",
            },
            {
              key: "qualified_week",
              label: this.$t("ncrm.leads.home.boardWidget.metrics.qualifiedWeek"),
              value: parseNumeric(metrics.qualified_week, 0),
              icon: "mdi-chart-line",
              color: "success",
            },
          ],
        });
        seen.add(boardId);
      }

      for (const [boardId, metrics] of this.boardMetricMap.entries()) {
        if (seen.has(boardId)) continue;
        items.push({
          id: boardId,
          name: metrics.board_name || `#${boardId}`,
          badgeText: this.entityScopeLabel(metrics.entity_scope || "both"),
          primaryLabel: this.$t("ncrm.leads.home.boardWidget.metrics.openEntries"),
          primaryValue: parseNumeric(metrics.open_entries, 0),
          primaryIcon: "mdi-filter-outline",
          primaryColor: "primary",
          metrics: [
            {
              key: "entry_count",
              label: this.$t("ncrm.leads.home.headers.totalEntries"),
              value: parseNumeric(metrics.entry_count, 0),
              icon: "mdi-database-outline",
              color: "quaternary",
            },
            {
              key: "overdue_followups",
              label: this.$t("ncrm.leads.home.boardWidget.metrics.overdueFollowups"),
              value: parseNumeric(metrics.overdue_followups, 0),
              icon: "mdi-alert-circle-outline",
              color: "error",
            },
            {
              key: "touched_today",
              label: this.$t("ncrm.leads.home.boardWidget.metrics.touchedToday"),
              value: parseNumeric(metrics.touched_today, 0),
              icon: "mdi-clock-outline",
              color: "info",
            },
            {
              key: "qualified_week",
              label: this.$t("ncrm.leads.home.boardWidget.metrics.qualifiedWeek"),
              value: parseNumeric(metrics.qualified_week, 0),
              icon: "mdi-chart-line",
              color: "success",
            },
          ],
        });
      }

      return items;
    },
    trendSeries() {
      const rows = Array.isArray(this.homeTrend?.series) ? this.homeTrend.series : [];
      const maxTotal = Math.max(
        1,
        ...rows.map((row) => parseNumeric(row?.total, 0))
      );
      return rows.map((row) => {
        const total = parseNumeric(row?.total, 0);
        const percent = total > 0 ? Math.max((total / maxTotal) * 100, 8) : 0;
        return {
          date: String(row?.date || "").trim(),
          label: this.formatTrendDate(row?.date),
          total,
          total_percent: percent,
        };
      });
    },
    trendDelta() {
      const totals = this.homeTrend?.totals || {};
      const deltaTotal = parseNumeric(totals?.delta_total, 0);
      const deltaPercentRaw = totals?.delta_percent;
      const deltaPercent = deltaPercentRaw === null || deltaPercentRaw === undefined
        ? null
        : parseNumeric(deltaPercentRaw, 0);
      return {
        delta_total: deltaTotal,
        delta_percent: deltaPercent,
        delta_percent_label: deltaPercent === null ? "-" : `${this.formatNumber(deltaPercent)}%`,
      };
    },
  },
  mounted() {
    this.restoreWidgetConfig();
    this.loadHome();
  },
  methods: {
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        color,
        message,
      };
    },
    formatNumber(value) {
      const number = parseNumeric(value, 0);
      return formatDisplayNumber(number);
    },
    formatSignedNumber(value) {
      const number = parseNumeric(value, 0);
      if (number > 0) return `+${this.formatNumber(number)}`;
      return this.formatNumber(number);
    },
    formatDateTime(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "-";
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return normalized;
      const dd = String(parsed.getDate()).padStart(2, "0");
      const mm = String(parsed.getMonth() + 1).padStart(2, "0");
      const yyyy = parsed.getFullYear();
      const HH = String(parsed.getHours()).padStart(2, "0");
      const min = String(parsed.getMinutes()).padStart(2, "0");
      return `${dd}.${mm}.${yyyy}, ${HH}:${min}`;
    },
    formatTrendDate(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "-";
      const parsed = new Date(`${normalized}T00:00:00`);
      if (Number.isNaN(parsed.getTime())) return normalized;
      return formatDisplayDayMonth(parsed);
    },
    entityScopeLabel(value) {
      const normalized = normalizeNcrmLeadEntityScope(value, "both") || "both";
      return this.$t(`ncrm.leads.entityScope.${normalized}`);
    },
    statusLabel(value) {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.leads.statuses.${normalized}`;
      const label = this.$t(key);
      return label === key ? normalized : label;
    },
    boardMetricValue(boardId, metricKey, fallback = 0) {
      const normalizedBoardId = Number(boardId || 0);
      if (!normalizedBoardId) return parseNumeric(fallback, 0);
      const metrics = this.boardMetricMap.get(normalizedBoardId);
      if (!metrics) return parseNumeric(fallback, 0);
      return parseNumeric(metrics?.[metricKey], parseNumeric(fallback, 0));
    },
    tableRowProps() {
      return { style: "cursor: pointer" };
    },
    resolveTimezoneOffsetMinutes() {
      return -new Date().getTimezoneOffset();
    },
    addWidget(key) {
      const normalized = String(key || "").trim();
      if (!LEADS_HOME_WIDGET_KEYS.includes(normalized)) return;
      if (this.activeWidgetKeys.includes(normalized)) return;
      this.activeWidgetKeys = [...this.activeWidgetKeys, normalized];
      this.persistWidgetConfig();
    },
    removeWidget(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return;
      this.activeWidgetKeys = this.activeWidgetKeys.filter((item) => item !== normalized);
      this.persistWidgetConfig();
    },
    moveWidget(key, direction) {
      const normalized = String(key || "").trim();
      const delta = Number(direction || 0);
      const currentIndex = this.activeWidgetKeys.findIndex((item) => item === normalized);
      if (currentIndex === -1) return;
      const targetIndex = currentIndex + (delta < 0 ? -1 : 1);
      if (targetIndex < 0 || targetIndex >= this.activeWidgetKeys.length) return;
      const next = [...this.activeWidgetKeys];
      const [item] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, item);
      this.activeWidgetKeys = next;
      this.persistWidgetConfig();
    },
    persistWidgetConfig() {
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(
          LEADS_HOME_WIDGET_STORAGE_KEY,
          JSON.stringify({ active_keys: this.activeWidgetKeys })
        );
      } catch (_) {
        // ignore storage failures
      }
    },
    restoreWidgetConfig() {
      if (typeof window === "undefined") return;
      try {
        const raw = window.localStorage.getItem(LEADS_HOME_WIDGET_STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return;

        const activeKeysFromV2 = Array.isArray(parsed?.active_keys)
          ? parsed.active_keys
              .map((item) => String(item || "").trim())
              .filter((item, index, source) => item && source.indexOf(item) === index)
              .filter((item) => LEADS_HOME_WIDGET_KEYS.includes(item))
          : null;
        if (activeKeysFromV2) {
          this.activeWidgetKeys = activeKeysFromV2;
          return;
        }

        const activeKeysFromV1 = LEADS_HOME_WIDGET_KEYS.filter((item) => Boolean(parsed?.[item]));
        this.activeWidgetKeys = activeKeysFromV1;
      } catch (_) {
        this.activeWidgetKeys = cloneDefaultActiveWidgetKeys();
      }
    },
    async loadHome() {
      this.boardsLoading = true;
      this.widgetsLoading = true;
      try {
        const timezoneOffsetMinutes = this.resolveTimezoneOffsetMinutes();
        const [boardsResult, summaryResult, trendResult] = await Promise.allSettled([
          axios.get("/ncrm/leads/boards"),
          axios.get("/ncrm/leads/home/summary", {
            params: {
              timezone_offset_minutes: timezoneOffsetMinutes,
            },
          }),
          axios.get("/ncrm/leads/home/trend", {
            params: {
              days: 7,
              timezone_offset_minutes: timezoneOffsetMinutes,
            },
          }),
        ]);

        const failures = [];

        if (boardsResult.status === "fulfilled") {
          this.boards = Array.isArray(boardsResult.value?.data?.items) ? boardsResult.value.data.items : [];
        } else {
          failures.push("boards");
          console.error("[ncrm-leads] load boards failed:", boardsResult.reason);
          this.boards = [];
        }

        if (summaryResult.status === "fulfilled") {
          this.homeSummary = {
            totals: summaryResult.value?.data?.totals || {},
            status_distribution: Array.isArray(summaryResult.value?.data?.status_distribution)
              ? summaryResult.value.data.status_distribution
              : [],
            board_metrics: Array.isArray(summaryResult.value?.data?.board_metrics)
              ? summaryResult.value.data.board_metrics
              : [],
          };
        } else {
          failures.push("summary");
          console.error("[ncrm-leads] load summary widgets failed:", summaryResult.reason);
          this.homeSummary = {
            totals: {},
            status_distribution: [],
            board_metrics: [],
          };
        }

        if (trendResult.status === "fulfilled") {
          this.homeTrend = {
            totals: trendResult.value?.data?.totals || {},
            series: Array.isArray(trendResult.value?.data?.series) ? trendResult.value.data.series : [],
          };
        } else {
          failures.push("trend");
          console.error("[ncrm-leads] load trend widgets failed:", trendResult.reason);
          this.homeTrend = {
            totals: {},
            series: [],
          };
        }

        if (failures.length) {
          const messageKey = failures.length === 3
            ? "ncrm.leads.home.messages.loadError"
            : "ncrm.leads.home.messages.partialLoadError";
          this.showSnackbar(this.$t(messageKey), failures.length === 3 ? "error" : "warning");
        }
      } catch (error) {
        console.error("[ncrm-leads] load home failed:", error);
        this.showSnackbar(this.$t("ncrm.leads.home.messages.loadError"), "error");
      } finally {
        this.boardsLoading = false;
        this.widgetsLoading = false;
      }
    },
    openCreateBoard() {
      this.createBoardDialog = true;
    },
    openBoard(boardId) {
      const id = Number(boardId || 0);
      if (!id) return;
      this.$router.push({
        name: "NcrmLeadBoard",
        params: { id: String(id) },
      });
    },
    handleBoardRowClick(_event, payload) {
      const row = payload?.item?.raw ?? payload?.item;
      this.openBoard(row?.id);
    },
    async createBoard(payload) {
      this.createBoardLoading = true;
      try {
        const { data } = await axios.post("/ncrm/leads/boards", payload || {});
        const boardId = Number(data?.board?.id || 0);
        this.createBoardDialog = false;
        await this.loadHome();
        this.showSnackbar(this.$t("ncrm.leads.home.messages.createSuccess"), "success");
        if (boardId) {
          this.openBoard(boardId);
        }
      } catch (error) {
        console.error("[ncrm-leads] create board failed:", error);
        const apiMessage = String(error?.response?.data?.error || "").trim();
        this.showSnackbar(apiMessage || this.$t("ncrm.leads.home.messages.createError"), "error");
      } finally {
        this.createBoardLoading = false;
      }
    },
  },
};
</script>

<style scoped src="./NcrmLeadsHome.styles.css"></style>
